import "../styles.css";
import { initKeycloak, PREVIEW_MODE, getEmail } from "../keycloak";
import { renderChrome, escapeHtml } from "../chrome";
import { apiFetch, errorMessage } from "../api";
import { formatDate, formatRelative } from "../lib/format";
import { confirmDialog, choiceDialog, formDialog } from "../lib/dialog";
import { mountPeoplePicker, type Directory, type Selection } from "../lib/people-picker";
import { putToS3, pickFile } from "../lib/s3-upload";
import { prepareUpload, UPLOAD_ACCEPT } from "../lib/artifact-build";
import { showSplash, hideSplash } from "../lib/splash";
import { openStatsPanel } from "../lib/stats-panel";
import { renderThumbnail } from "../lib/thumbnail";
import type { ReportSummary, ReportsListResponse, ReportStatus } from "../lib/types";

const PREVIEW_EMAIL = "luke@twentyfirstgroup.com";

const PREVIEW_REPORTS: ReportSummary[] = [
  {
    uploadId: "01HYZ8X9ABCDEFGHJKMNPQRSTV",
    title: "LIV Golf Virginia 2026 strategic brief",
    clientSlug: "liv-golf",
    reportSlug: "virginia-2026-brief",
    uploadedBy: "luke@twentyfirstgroup.com",
    uploadedAt: "2026-05-12T14:23:00Z",
    expiresAt: "2026-05-19T14:23:00Z",
    status: "active",
    hitCount: 47,
    lastHitAt: "2026-05-17T09:14:00Z",
    kind: "report" as const,
    revisionCount: 1,
    isOwner: true,
    canManage: true,
  },
  {
    uploadId: "01HYZ8X9ABCDEFGHJKMNPQRSTW",
    title: "Mexico 2026 media pack — landing analysis",
    clientSlug: "liv-golf",
    reportSlug: "mexico-2026-media-pack",
    uploadedBy: "lara@twentyfirstgroup.com",
    uploadedAt: "2026-05-08T09:12:00Z",
    expiresAt: "2026-06-07T09:12:00Z",
    status: "active",
    hitCount: 12,
    lastHitAt: "2026-05-16T18:33:00Z",
    kind: "report" as const,
    revisionCount: 1,
    isOwner: false,
    canManage: false,
  },
  {
    uploadId: "01HYZ8X9ABCDEFGHJKMNPQRSTX",
    title: "TGL 2025/26 season intelligence",
    clientSlug: "tgl",
    reportSlug: "season-25-26-intel",
    uploadedBy: "daniel@twentyfirstgroup.com",
    uploadedAt: "2026-04-29T16:48:00Z",
    expiresAt: "2026-05-06T16:48:00Z",
    status: "expired",
    hitCount: 8,
    lastHitAt: "2026-05-05T13:20:00Z",
    kind: "report" as const,
    revisionCount: 1,
    isOwner: false,
    canManage: false,
  },
  {
    uploadId: "01HYZ8X9ABCDEFGHJKMNPQRSTY",
    title: "Aramco PIF investment thesis draft",
    clientSlug: "pif",
    reportSlug: "investment-thesis-q2",
    uploadedBy: "tim@twentyfirstgroup.com",
    uploadedAt: "2026-04-15T11:05:00Z",
    expiresAt: "2026-05-15T11:05:00Z",
    status: "revoked",
    hitCount: 3,
    lastHitAt: "2026-04-20T08:50:00Z",
    kind: "report" as const,
    revisionCount: 1,
    isOwner: false,
    canManage: false,
  },
];

interface DashboardState {
  reports: ReportSummary[];
  filter: string;
  loading: boolean;
  /**
   * uploadId from ?report=... — the expiry-notifier's emails link straight to
   * the report they are about, so the consultant lands on the right row
   * instead of hunting a table of everything TFG has ever shared.
   *
   * Cleared once applied, so Refresh and filtering don't keep yanking the
   * viewport back to it.
   */
  highlightUploadId?: string;
}

const state: DashboardState = { reports: [], filter: "", loading: true };

async function bootstrap(): Promise<void> {
  showSplash("Signing in");
  const authed = await initKeycloak();
  if (!authed) return;
  hideSplash();

  renderChrome("dashboard");

  const main = document.getElementById("main");
  if (!main) return;

  main.innerHTML = `
    <div class="max-w-7xl mx-auto px-6 py-10">
      <div class="flex items-end justify-between mb-8">
        <div>
          <h1 class="text-2xl font-semibold tracking-tight">Reports</h1>
          <p class="text-sm text-bone-muted mt-1">Every report uploaded across TFG.</p>
        </div>
        <a href="/upload.html" class="btn-primary">+ New report</a>
      </div>

      <div class="flex items-center gap-3 mb-8">
        <input id="filter" class="input max-w-xs" placeholder="Filter by client slug…" />
        <button id="refresh" class="btn-ghost">Refresh</button>
      </div>

      <section id="your-reports-section" class="mb-10 hidden">
        <div class="flex items-baseline justify-between mb-3">
          <h2 class="text-xs uppercase tracking-wider text-bone-muted font-medium">Your reports</h2>
          <span id="your-reports-count" class="text-xs text-bone-muted font-mono"></span>
        </div>
        <div id="your-reports-container" class="card overflow-hidden"></div>
      </section>

      <section>
        <div class="flex items-baseline justify-between mb-3">
          <h2 class="text-xs uppercase tracking-wider text-bone-muted font-medium">All reports</h2>
          <span id="all-reports-count" class="text-xs text-bone-muted font-mono"></span>
        </div>
        <div id="table-container" class="card overflow-hidden"></div>
      </section>
    </div>
  `;

  document.getElementById("filter")?.addEventListener("input", (e) => {
    state.filter = (e.target as HTMLInputElement).value.trim().toLowerCase();
    renderTable();
  });
  document.getElementById("refresh")?.addEventListener("click", () => void loadReports());

  const deepLink = new URLSearchParams(window.location.search).get("report");
  if (deepLink) state.highlightUploadId = deepLink;

  await loadReports();
}

/**
 * Bring the deep-linked row into view and mark it.
 *
 * A report can appear twice — once under "Your reports" and once under "All
 * reports" — so both copies are marked and the first is what we scroll to.
 */
function applyDeepLink(): void {
  const target = state.highlightUploadId;
  if (!target) return;

  const rows = document.querySelectorAll<HTMLElement>(`tr[data-id="${CSS.escape(target)}"]`);
  if (rows.length === 0) return;

  rows.forEach((row) => row.classList.add("ring-1", "ring-gold", "bg-ink/60"));
  rows[0]?.scrollIntoView({ behavior: "smooth", block: "center" });

  // One-shot: the highlight stays on screen, but a later re-render won't
  // re-scroll or fight the user's own filtering.
  state.highlightUploadId = undefined;
}

async function loadReports(): Promise<void> {
  state.loading = true;
  renderTable();
  try {
    if (PREVIEW_MODE) {
      state.reports = PREVIEW_REPORTS;
    } else {
      const res = await apiFetch<ReportsListResponse>("/api/reports");
      state.reports = res.items;
    }
  } catch (err) {
    state.reports = [];
    console.error(err);
  } finally {
    state.loading = false;
    renderTable();
  }
}

function renderTable(): void {
  const yoursContainer = document.getElementById("your-reports-container");
  const yoursSection = document.getElementById("your-reports-section");
  const yoursCount = document.getElementById("your-reports-count");
  const allContainer = document.getElementById("table-container");
  const allCount = document.getElementById("all-reports-count");
  if (!yoursContainer || !yoursSection || !allContainer) return;

  if (state.loading) {
    allContainer.innerHTML = `<div class="p-12 text-center text-sm text-bone-muted">Loading…</div>`;
    yoursSection.classList.add("hidden");
    return;
  }

  const filtered = state.filter
    ? state.reports.filter((r) =>
        `${r.clientSlug} ${r.reportSlug} ${r.title}`.toLowerCase().includes(state.filter),
      )
    : state.reports;

  const currentEmail = (PREVIEW_MODE ? PREVIEW_EMAIL : getEmail())?.toLowerCase();
  const yours = currentEmail
    ? filtered.filter((r) => r.uploadedBy.toLowerCase() === currentEmail)
    : [];

  // "Your reports" section
  if (yours.length > 0) {
    yoursSection.classList.remove("hidden");
    if (yoursCount) yoursCount.textContent = String(yours.length);
    yoursContainer.innerHTML = renderTableHtml(yours);
  } else {
    yoursSection.classList.add("hidden");
  }

  // "All reports" section
  if (allCount) allCount.textContent = String(filtered.length);
  if (filtered.length === 0) {
    allContainer.innerHTML = `
      <div class="p-16 text-center">
        <p class="text-sm text-bone-muted mb-4">${
          state.reports.length === 0 ? "No reports uploaded yet." : "No reports match the filter."
        }</p>
        ${state.reports.length === 0 ? `<a href="/upload.html" class="btn-primary inline-flex">Upload the first report</a>` : ""}
      </div>
    `;
  } else {
    allContainer.innerHTML = renderTableHtml(filtered);
  }

  bindRowActions();
  applyDeepLink();
}

function renderTableHtml(rows: ReportSummary[]): string {
  return `
    <table class="w-full text-sm">
      <thead class="text-xs uppercase tracking-wider text-bone-muted border-b border-ink-border">
        <tr>
          <th class="px-4 py-3 text-left font-medium w-24"></th>
          <th class="px-4 py-3 text-left font-medium">Title</th>
          <th class="px-4 py-3 text-left font-medium">Client / report</th>
          <th class="px-4 py-3 text-left font-medium">Uploaded</th>
          <th class="px-4 py-3 text-left font-medium">Expires</th>
          <th class="px-4 py-3 text-right font-medium">Views</th>
          <th class="px-4 py-3 text-left font-medium">Status</th>
          <th class="px-4 py-3 text-right font-medium">Actions</th>
        </tr>
      </thead>
      <tbody>
        ${rows.map(renderRow).join("")}
      </tbody>
    </table>
  `;
}

function renderRow(r: ReportSummary): string {
  const badgeClass = badge(r.status);
  const viewsCell =
    r.hitCount > 0
      ? `
        <div class="font-semibold tabular-nums">${r.hitCount}</div>
        <div class="text-xs text-bone-muted">${r.lastHitAt ? escapeHtml(formatRelative(r.lastHitAt)) : ""}</div>
      `
      : `<div class="text-bone-muted text-xs">Not yet</div>`;

  return `
    <tr data-action="stats" data-c="${r.clientSlug}" data-r="${r.reportSlug}" data-id="${r.uploadId}" data-title="${escapeHtml(r.title)}"
        class="border-b border-ink-border last:border-0 hover:bg-ink/40 transition-colors cursor-pointer">
      <td class="px-4 py-3">
        ${renderThumbnail({ thumbnailUrl: r.thumbnailUrl, clientSlug: r.clientSlug, reportSlug: r.reportSlug })}
      </td>
      <td class="px-4 py-3">
        <div class="text-bone">
          ${escapeHtml(r.title)}
          ${
            r.revisionCount > 1
              ? `<span class="ml-1.5 text-[10px] font-mono text-bone-muted align-middle"
                       title="Updated ${r.updatedAt ? escapeHtml(formatRelative(r.updatedAt)) : ""} — the client's link is unchanged">v${r.revisionCount}</span>`
              : ""
          }
        </div>
        <div class="text-xs text-bone-muted mt-0.5">${escapeHtml(r.uploadedBy)}</div>
      </td>
      <td class="px-4 py-3 font-mono text-xs">
        <div class="text-bone">${escapeHtml(r.clientSlug)}</div>
        <div class="text-bone-muted mt-0.5">${escapeHtml(r.reportSlug)}</div>
      </td>
      <td class="px-4 py-3 text-bone-muted">
        ${formatDate(r.uploadedAt)}
      </td>
      <td class="px-4 py-3 text-bone-muted">
        ${formatDate(r.expiresAt)}
        <div class="text-xs">${escapeHtml(formatRelative(r.expiresAt))}</div>
      </td>
      <td class="px-4 py-3 text-right">
        ${viewsCell}
      </td>
      <td class="px-4 py-3">
        <span class="${badgeClass}">${r.status}</span>
      </td>
      <td class="px-4 py-3 text-right" data-noprop="1">
        <div class="inline-flex gap-2 items-center">
          <button data-action="copy" data-c="${r.clientSlug}" data-r="${r.reportSlug}" data-id="${r.uploadId}"
                  class="btn-ghost h-8 px-2.5 text-xs ${r.status !== "active" ? "opacity-30 pointer-events-none" : ""}">
            Copy URL
          </button>
          ${
            // Copy and stats stay open to everyone — reads are shared, which is
            // what lets a colleague cover for you. Anything that changes or
            // destroys the report is owner-or-admin, enforced by the API; this
            // only decides what's worth showing. See lib/authz.ts.
            r.canManage
              ? `
            ${
              r.status === "active"
                ? `
              ${
                r.kind === "app"
                  ? `<button data-action="access" data-c="${r.clientSlug}" data-r="${r.reportSlug}" data-id="${r.uploadId}" data-title="${escapeHtml(r.title)}"
                             class="btn-ghost h-8 px-2.5 text-xs">
                       Access
                     </button>`
                  : ""
              }
              <button data-action="update" data-c="${r.clientSlug}" data-r="${r.reportSlug}" data-id="${r.uploadId}" data-title="${escapeHtml(r.title)}"
                      class="btn-ghost h-8 px-2.5 text-xs">
                Update
              </button>
              <button data-action="expiry" data-c="${r.clientSlug}" data-r="${r.reportSlug}" data-id="${r.uploadId}" data-expires="${escapeHtml(r.expiresAt)}"
                      class="btn-ghost h-8 px-2.5 text-xs">
                Expiry
              </button>
            `
                : ""
            }
            ${
              // An expired report is recoverable, and recovering it is the
              // common case — the pitch window moved, not the relationship.
              // /resign re-signs under the SAME short code, so the link the
              // client already has comes back to life at the same address.
              //
              // Without this button the only enabled control on an expired row
              // is "Delete forever", which is both the wrong default and the
              // one irreversible action. Gating renew on `status === "active"`
              // meant the dashboard offered no way back from the state it is
              // most likely to be in. The API has always allowed it — it
              // refuses only revoked rows — so this was a UI gap, not a
              // capability one.
              r.status === "expired"
                ? `
              <button data-action="expiry" data-c="${r.clientSlug}" data-r="${r.reportSlug}" data-id="${r.uploadId}" data-expires="${escapeHtml(r.expiresAt)}"
                      class="btn-primary h-8 px-2.5 text-xs">
                Renew
              </button>
            `
                : ""
            }
            ${
              r.status === "active" || r.status === "pending"
                ? `
              <button data-action="revoke" data-c="${r.clientSlug}" data-r="${r.reportSlug}" data-id="${r.uploadId}"
                      class="btn-danger h-8 px-2.5 text-xs">
                Revoke
              </button>
            `
                : `
              <button data-action="delete" data-c="${r.clientSlug}" data-r="${r.reportSlug}" data-id="${r.uploadId}" data-title="${escapeHtml(r.title)}"
                      class="btn-danger h-8 px-2.5 text-xs">
                Delete
              </button>
            `
            }
          `
              : `
            <span class="text-xs text-bone-muted italic pr-1" title="Only ${escapeHtml(r.uploadedBy)} or an admin can change this report">
              ${escapeHtml(ownerFirstName(r.uploadedBy))}'s
            </span>
          `
          }
        </div>
      </td>
    </tr>
  `;
}

/**
 * "lara@twentyfirstgroup.com" -> "Lara". Used to say whose report it is
 * without a full address crowding the actions column; the exact owner is
 * already shown under the title and in the tooltip.
 */
function ownerFirstName(email: string): string {
  const local = (email ?? "").split("@")[0] ?? "";
  const first = local.split(/[.\-_+]/)[0] ?? local;
  return first ? first.charAt(0).toUpperCase() + first.slice(1) : "Someone";
}

function badge(status: ReportStatus): string {
  switch (status) {
    case "active":
      return "badge-active";
    case "pending":
      return "badge-pending";
    case "revoked":
      return "badge-revoked";
    case "expired":
    default:
      return "badge-expired";
  }
}

function bindRowActions(): void {
  document.querySelectorAll<HTMLButtonElement>("[data-action='copy']").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      void onCopy(btn);
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-action='access']").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      void onAccess(btn);
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-action='update']").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      void onUpdate(btn);
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-action='expiry']").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      void onExpiry(btn);
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-action='revoke']").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      void onRevoke(btn);
    });
  });
  document.querySelectorAll<HTMLButtonElement>("[data-action='delete']").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      void onDelete(btn);
    });
  });
  document.querySelectorAll<HTMLTableRowElement>("tr[data-action='stats']").forEach((row) => {
    row.addEventListener("click", () => {
      const {
        c,
        r: rSlug,
        id,
        title,
      } = row.dataset as {
        c: string;
        r: string;
        id: string;
        title: string;
      };
      void openStatsPanel({ clientSlug: c, reportSlug: rSlug, uploadId: id, title });
    });
  });
}

async function onCopy(btn: HTMLButtonElement): Promise<void> {
  if (PREVIEW_MODE) {
    flashButton(btn, "API disabled");
    return;
  }
  const { c, r, id } = btn.dataset as { c: string; r: string; id: string };
  const original = btn.textContent;
  btn.disabled = true;
  btn.textContent = "Signing…";
  try {
    const res = await apiFetch<{ url: string }>(`/api/reports/${c}/${r}/${id}/url`, {
      method: "POST",
    });
    await navigator.clipboard.writeText(res.url);
    btn.textContent = "Copied";
    setTimeout(() => {
      btn.textContent = original;
      btn.disabled = false;
    }, 1800);
  } catch (err) {
    console.error(err);
    btn.textContent = "Failed";
    setTimeout(() => {
      btn.textContent = original;
      btn.disabled = false;
    }, 2000);
  }
}

/**
 * Change who can open an app, after it has been uploaded.
 *
 * Without this the allowlist was fixed at upload: adding one person meant
 * re-uploading the whole app, and removing someone was impossible short of
 * revoking it.
 */
async function onAccess(btn: HTMLButtonElement): Promise<void> {
  if (PREVIEW_MODE) {
    flashButton(btn, "API disabled");
    return;
  }
  const { c, r, id, title } = btn.dataset as { c: string; r: string; id: string; title: string };

  const row = state.reports.find((x) => x.uploadId === id);
  const initial: Selection = {
    emails: row?.allowedEmails ?? [],
    groups: row?.allowedGroups ?? [],
  };

  const original = btn.textContent;
  btn.disabled = true;
  btn.textContent = "Loading…";

  let directory: Directory;
  try {
    directory = await apiFetch<Directory>("/api/directory");
  } catch (err) {
    btn.disabled = false;
    btn.textContent = original;
    await confirmDialog({
      title: "Couldn't load the people list",
      body: errorMessage(err),
      confirmLabel: "Close",
      cancelLabel: "Dismiss",
    });
    return;
  }
  btn.disabled = false;
  btn.textContent = original;

  let selection: Selection = initial;
  const saved = await formDialog({
    title: `Who can open "${title}"`,
    body: "Leave empty for anyone at TFG. You can always open it yourself, whether or not you're listed.",
    confirmLabel: "Save access",
    mount: (container) => {
      mountPeoplePicker(container, directory, (sel) => (selection = sel), initial);
    },
  });
  if (!saved) return;

  btn.disabled = true;
  btn.textContent = "Saving…";
  try {
    await apiFetch(`/api/reports/${c}/${r}/${id}/sharing`, {
      method: "POST",
      body: JSON.stringify({
        allowedEmails: selection.emails,
        allowedGroups: selection.groups,
      }),
    });
    await loadReports();
  } catch (err) {
    console.error(err);
    btn.disabled = false;
    btn.textContent = original;
    await confirmDialog({
      title: "Couldn't update access",
      body: errorMessage(err),
      confirmLabel: "Close",
      cancelLabel: "Dismiss",
    });
  }
}

/**
 * Replace the HTML behind an existing report, keeping the same link.
 *
 * The uploaded file goes to a staging key first and is only promoted onto the
 * live path once the server has checked its size and type, so a wrong file
 * never reaches the client. The client's link, the expiry and the view stats
 * all carry across — only the content changes.
 */
async function onUpdate(btn: HTMLButtonElement): Promise<void> {
  if (PREVIEW_MODE) {
    flashButton(btn, "API disabled");
    return;
  }
  const { c, r, id, title } = btn.dataset as {
    c: string;
    r: string;
    id: string;
    title: string;
  };

  const confirmed = await confirmDialog({
    title: "Update this report?",
    body: `Pick a new file for "${title}" — an .html report, or the .jsx if this is a Claude app.

The client's existing link starts serving the new version as soon as this finishes — they do not need a new link, and you cannot undo it from here. The expiry and the view history stay as they are.`,
    confirmLabel: "Choose file…",
  });
  if (!confirmed) return;

  // Same types the upload page takes. An app is normally updated from the
  // .jsx it came from, so offering only .html greyed out the very file the
  // consultant has.
  const file = await pickFile(UPLOAD_ACCEPT);
  if (!file) return;

  const original = btn.textContent;
  btn.disabled = true;
  btn.textContent = "Preparing…";
  try {
    const init = await apiFetch<{ stagingKey: string; presignedPutUrl: string }>(
      `/api/reports/${c}/${r}/${id}/replace`,
      { method: "POST" },
    );

    // Compile first if they picked a .jsx — otherwise this would publish React
    // source, which no browser can run, and the app would go blank.
    const upload = await prepareUpload(file, (stage) => {
      btn.textContent = stage.replace(/…$/, "…");
    });

    await putToS3(init.presignedPutUrl, upload, (pct) => {
      btn.textContent = `${Math.round(pct * 100)}%`;
    });

    btn.textContent = "Publishing…";
    const res = await apiFetch<{ revisionCount: number }>(
      `/api/reports/${c}/${r}/${id}/replace-finalize`,
      { method: "POST", body: JSON.stringify({ stagingKey: init.stagingKey }) },
    );

    btn.textContent = `v${res.revisionCount}`;
    await loadReports();
  } catch (err) {
    console.error(err);
    btn.disabled = false;
    btn.textContent = original;
    await confirmDialog({
      title: "Could not update this report",
      // The API says explicitly when the live report is untouched, which is
      // the first thing you want to know when an update fails.
      body: errorMessage(err),
      confirmLabel: "Close",
      cancelLabel: "Dismiss",
    });
  }
}

/**
 * Change how long a report stays live, or bring an expired one back.
 *
 * Calls the existing /resign endpoint, which has always accepted an
 * expiryDays of 7/30/90 but had no UI — the only way to change an expiry was
 * to revoke and re-upload, which broke the client's link. The short code is
 * stable across a re-sign, so the link the client already has keeps working
 * with the new expiry.
 *
 * That stability is what makes this the recovery path for an expired report
 * too: re-signing restores the original /s/{code} address rather than minting
 * a new one, so a link already sitting in a client's inbox starts working
 * again. Expiry only ever moved a date — it never deleted the object or the
 * short-code row — so there is nothing to rebuild. Revoke is the one that
 * deletes, and the API refuses to re-sign those.
 */
async function onExpiry(btn: HTMLButtonElement): Promise<void> {
  if (PREVIEW_MODE) {
    flashButton(btn, "API disabled");
    return;
  }
  const { c, r, id, expires } = btn.dataset as {
    c: string;
    r: string;
    id: string;
    expires: string;
  };

  // Same endpoint either way; only the framing differs. Telling someone their
  // client's link is dead right now — and that this is what revives it — is
  // the whole difference between the two cases.
  const isExpired = new Date(expires).getTime() < Date.now();
  const originalLabel = btn.textContent ?? "Expiry";

  const choice = await choiceDialog<"7" | "30" | "90">({
    title: isExpired ? "Renew this link" : "Set expiry",
    body: isExpired
      ? `Expired ${formatDate(expires)} (${formatRelative(expires)}). Anyone opening the link right now gets an "expired" page.

Renewing restores the same link — the client does not need a new one. Counting starts from now.`
      : `Currently expires ${formatDate(expires)} (${formatRelative(expires)}).

The client keeps the same link — only the expiry moves. Counting starts from now, so picking 7 days means seven days from today, not seven from the original upload.`,
    options: [
      { value: "7", label: "7 days", note: "Short-turnaround share" },
      { value: "30", label: "30 days", note: "Typical pitch window" },
      { value: "90", label: "90 days", note: "Long-running engagement" },
    ],
    initial: "30",
    confirmLabel: isExpired ? "Renew link" : "Update expiry",
  });
  if (!choice) return;

  btn.disabled = true;
  btn.textContent = isExpired ? "Renewing…" : "Updating…";
  try {
    await apiFetch(`/api/reports/${c}/${r}/${id}/resign`, {
      method: "POST",
      body: JSON.stringify({ expiryDays: Number(choice) }),
    });
    await loadReports();
  } catch (err) {
    console.error(err);
    btn.disabled = false;
    // Restore what the button actually said — this handler now backs both the
    // "Expiry" and "Renew" buttons, and hardcoding either one relabels the
    // other on the way out.
    btn.textContent = originalLabel;
    await confirmDialog({
      title: isExpired ? "Could not renew this link" : "Could not update the expiry",
      body: errorMessage(err),
      confirmLabel: "Close",
      cancelLabel: "Dismiss",
    });
  }
}

async function onRevoke(btn: HTMLButtonElement): Promise<void> {
  if (PREVIEW_MODE) {
    flashButton(btn, "API disabled");
    return;
  }
  const { c, r, id } = btn.dataset as { c: string; r: string; id: string };
  const confirmed = await confirmDialog({
    title: "Revoke this report?",
    body: `The link to ${c} / ${r} will stop working immediately for anyone who has it.

The S3 object is removed and the CloudFront cache is invalidated. The report row stays on the dashboard with a "REVOKED" badge until you permanently delete it.`,
    confirmLabel: "Revoke",
    danger: true,
  });
  if (!confirmed) return;

  btn.disabled = true;
  btn.textContent = "Revoking…";
  try {
    await apiFetch(`/api/reports/${c}/${r}/${id}/revoke`, { method: "POST" });
    await loadReports();
  } catch (err) {
    console.error(err);
    btn.textContent = "Revoke";
    btn.disabled = false;
    // Surface the reason rather than leaving "Failed" on the button. Since
    // reports became owner-only, a 403 carrying "Only lara@... can do this"
    // is a routine outcome, and it is useless in the console.
    await confirmDialog({
      title: "Could not revoke this report",
      body: errorMessage(err),
      confirmLabel: "Close",
      cancelLabel: "Dismiss",
    });
  }
}

async function onDelete(btn: HTMLButtonElement): Promise<void> {
  if (PREVIEW_MODE) {
    flashButton(btn, "API disabled");
    return;
  }
  const { c, r, id, title } = btn.dataset as { c: string; r: string; id: string; title: string };
  const confirmed = await confirmDialog({
    title: `Permanently delete "${title || `${c}/${r}`}"?`,
    body: `This wipes the S3 object including its full version history and removes the report from the dashboard.

It cannot be undone.`,
    confirmLabel: "Delete forever",
    danger: true,
    typedConfirmation: "DELETE",
  });
  if (!confirmed) return;

  btn.disabled = true;
  btn.textContent = "Deleting…";
  try {
    await apiFetch(`/api/reports/${c}/${r}/${id}/remove`, { method: "POST" });
    await loadReports();
  } catch (err) {
    console.error(err);
    btn.textContent = "Delete";
    btn.disabled = false;
    // Surface the reason rather than leaving "Failed" on the button. Since
    // reports became owner-only, a 403 carrying "Only lara@... can do this"
    // is a routine outcome, and it is useless in the console.
    await confirmDialog({
      title: "Could not delete this report",
      body: errorMessage(err),
      confirmLabel: "Close",
      cancelLabel: "Dismiss",
    });
  }
}

function flashButton(btn: HTMLButtonElement, msg: string): void {
  const original = btn.textContent;
  btn.disabled = true;
  btn.textContent = msg;
  setTimeout(() => {
    btn.textContent = original;
    btn.disabled = false;
  }, 1500);
}

bootstrap().catch((err) => {
  console.error(err);
  document.body.innerHTML = `<pre class="p-6 text-red-300 font-mono text-xs">${String(err)}</pre>`;
});
